import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Container, Grid2, Button, Typography, TextField, MenuItem, Select, FormControl, InputLabel, Tabs, Tab, Box } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';
import Navbar from '../Navbar';
import Footer from '../Footer';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import ParkingTypesForm from './ParkingTypesForm';
import ApartmentEquipmentForm from './ApartmentEquipmentForm';
import KitchenEquipmentForm from './KitchenEquipmentForm';
import HomeGoodsForm from './HomeGoodsForm';
import RentOfferForm from './RentOfferForm';
import RentPreferencesForm from './RentPreferencesForm';
import { BUILDING_TYPE_CHOICES } from '../../constants/buildingTypes';

const EditApartment = () => {
  const { t } = useTranslation();
  const user = JSON.parse(localStorage.getItem('user'));
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    user: user ? user.id : '',
    square_footage: '',
    floor: '',
    rooms: '',
    levels: '',
    bedrooms: '',
    bathrooms: '',
    construction_year: '',
    building_type: '',
    internet: '',
    elevator: '',
    security_camera: '',
  });
  
  const [uploaded_photos, setUploadedPhotos] = useState([]);
  const [existing_photos, setExistingPhotos] = useState([]);
  const [deleted_photos, setDeletedPhotos] = useState([]);
  const [apartment, setApartment] = useState(null);
  const [token, setToken] = useState('');
  const [tabIndex, setTabIndex] = useState(0); // State to track active tab

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    const storedToken = userData?.token || '';
    setToken(storedToken);
  }, []);

  useEffect(() => {
    const fetchApartmentDetails = async () => {
      if (!token) return; // Exit early if no token
  
      try {
        const apartmentResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/apartments/${id}/`, {
          headers: {
            'Authorization': `Token ${token}`
          }
        });
        setApartment(apartmentResponse.data);
        setFormData({
          user: apartmentResponse.data.user,
          square_footage: apartmentResponse.data.square_footage,
          floor: apartmentResponse.data.floor,
          rooms: apartmentResponse.data.rooms,
          levels: apartmentResponse.data.levels,
          bedrooms: apartmentResponse.data.bedrooms,
          bathrooms: apartmentResponse.data.bathrooms,
          construction_year: apartmentResponse.data.construction_year,
          building_type: apartmentResponse.data.building_type,
          internet: apartmentResponse.data.internet,
          elevator: apartmentResponse.data.elevator,
          security_camera: apartmentResponse.data.security_camera,
        });
        setExistingPhotos(apartmentResponse.data.photoURL || []);
      } catch (error) {
        console.error('Error fetching apartment details:', error);
      }
    };

    fetchApartmentDetails();
  }, [id, token]); // Re-run effect if id or token changes

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handlePhotosChange = (event) => {
    const files = event.target.files;
    setUploadedPhotos(prevPhotos => [...prevPhotos, ...files]);
  };

  const handleDeletePhoto = (index) => {
    setUploadedPhotos((prevPhotos) => prevPhotos.filter((_, i) => i !== index));
  };

  const handleDeleteExistingPhoto = (index) => {
    const photoToDelete = existing_photos[index];
    setDeletedPhotos((prevDeletePhotos) => [...prevDeletePhotos, photoToDelete.id]);
    setExistingPhotos((prevPhotos) => prevPhotos.filter((_, i) => i !== index));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const formDataToSend = new FormData();
    // Append existing photos
    existing_photos.forEach((photoURL, index) => {
      formDataToSend.append(`existing[${index}]`, photoURL.id);
    });
    // Append deleted photos
    deleted_photos.forEach((photoID, index) => {
      formDataToSend.append(`deleted[${index}]`, photoID);
    });
    // Append new uploaded photos
    Array.from(uploaded_photos).forEach((photoURL, index) => {
      formDataToSend.append(`uploaded_photos[${index}]`, photoURL);
    });
    // Append form data fields if they are not empty
    const formFields = [
      'user', 'square_footage', 'floor', 'rooms', 'levels', 'bedrooms', 'bathrooms', 'construction_year', 'building_type', 'internet', 'elevator', 'security_camera'
    ];
    formFields.forEach(field => {
      if (formData[field] !== '' && formData[field] !== undefined) {
        formDataToSend.append(field, formData[field]);
      }
    });
    // Make the PUT request to update the apartment
    axios.put(`${process.env.REACT_APP_API_URL}/api/apartments/${id}/`, formDataToSend, {
      headers: {
        'Authorization': `Token ${token}`,
        'Content-Type': 'multipart/form-data',
      },
    }).then(response => {
      alert('The apartment updated successfully!');
      //navigate('/dashboard');
    }).catch(error => {
      console.error('Error updating apartment:', error);
      alert('Failed to update the apartment. Please try again.');
    });
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  if (!user) {
    return <div>No user data available.</div>;
  }
  
  if (!apartment) {
    return <div>Loading...</div>;
  }
  

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Navbar />
      <Container style={{ flex: '1 0 auto', marginTop: '2rem', marginBottom: '2rem', padding: '2rem' }}>
      <Box display="flex" alignItems="center">
          <IconButton onClick={() => navigate('/dashboard')} color="primary">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" gutterBottom>
            {t('editApartment.title')}
          </Typography>
      </Box>
      <Typography variant="body1" color="textSecondary" component="p">
        {apartment.place_description || t('editApartment.noDescription')}
      </Typography>
        <Tabs value={tabIndex} onChange={handleTabChange} centered>
          <Tab label={t('editApartment.general')} />
          <Tab label={t('editApartment.parking')} />
          <Tab label={t('editApartment.equipment')} />
          <Tab label={t('editApartment.kitchen')} />
          <Tab label={t('editApartment.homeGoods')} />
          <Tab label={t('editApartment.rentPreferences')} />
          <Tab label={t('editApartment.rentOffer')} />
        </Tabs>
        
        <Box sx={{ mt: 4 }}>
          {tabIndex === 0 && (
        <form onSubmit={handleSubmit}>
          <TextField
            id="square-footage"
            name="square_footage"
            label={t('addApartment.squareFootage')}
            type="number"
            value={formData.square_footage}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            id="floor"
            name="floor"
            label={t('addApartment.floor')}
            type="number"
            value={formData.floor}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            id="rooms"
            name="rooms"
            label={t('addApartment.rooms')}
            type="number"
            value={formData.rooms}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            id="levels-input"
            name="levels"
            label={t('addApartment.balcony')}
            type="number"
            value={formData.levels}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            id="bedrooms"
            name="bedrooms"
            label={t('addApartment.bedrooms')}
            type="number"
            value={formData.bedrooms}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            id="bathrooms"
            name="bathrooms"
            label={t('addApartment.bathrooms')}
            type="number"
            value={formData.bathrooms}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          <TextField
            id="construction-year"
            name="construction_year"
            label={t('addApartment.constructionDate')}
            type="date"
            value={formData.construction_year}
            onChange={handleChange}
            fullWidth
            margin="normal"
            InputLabelProps={{
              shrink: true,
            }}
          />
          <FormControl margin="normal" fullWidth>
            <InputLabel>{t('addApartment.buildingType')}</InputLabel>
            <Select
                value={formData.building_type}
                onChange={handleChange}
                label={t('addApartment.buildingType')}
                name="building_type"
              >
                {BUILDING_TYPE_CHOICES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
          </FormControl>
          <FormControl margin="normal" fullWidth>
            <InputLabel>{t('addApartment.internet')}</InputLabel>
            <Select
              label={t('addApartment.internet')}
              name="internet"
              value={formData.internet ? t('addApartment.yes') : t('addApartment.no')}
              onChange={(e) =>
                setFormData((prevState) => ({
                  ...prevState,
                  internet: e.target.value === t('addApartment.yes'),
                }))
              }
            >
              <MenuItem value={t('addApartment.yes')}>{t('addApartment.yes')}</MenuItem>
              <MenuItem value={t('addApartment.no')}>{t('addApartment.no')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl margin="normal" fullWidth>
            <InputLabel>{t('addApartment.elevator')}</InputLabel>
            <Select
              label={t('addApartment.elevator')}
              name="elevator"
              value={formData.elevator ? t('addApartment.yes') : t('addApartment.no')}
              onChange={(e) =>
                setFormData((prevState) => ({
                  ...prevState,
                  elevator: e.target.value === t('addApartment.yes'),
                }))
              }
            >
              <MenuItem value={t('addApartment.yes')}>{t('addApartment.yes')}</MenuItem>
              <MenuItem value={t('addApartment.no')}>{t('addApartment.no')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl margin="normal" fullWidth>
            <InputLabel>{t('addApartment.securityCamera')}</InputLabel>
            <Select
              label={t('addApartment.securityCamera')}
              name="security_camera"
              value={formData.security_camera ? t('addApartment.yes') : t('addApartment.no')}
              onChange={(e) =>
                setFormData((prevState) => ({
                  ...prevState,
                  security_camera: e.target.value === t('addApartment.yes'),
                }))
              }
            >
              <MenuItem value={t('addApartment.yes')}>{t('addApartment.yes')}</MenuItem>
              <MenuItem value={t('addApartment.no')}>{t('addApartment.no')}</MenuItem>
            </Select>
          </FormControl>
          <div style={{ margin: '20px 0' }}>
            <label htmlFor="photo-upload" style={{ cursor: 'pointer' }}>
              <Button
                variant="contained"
                color="primary"
                component="span"
              >
                {t('addApartment.addPhotos')}
              </Button>
            </label>
            <input
              id="photo-upload"
              type="file"
              multiple
              onChange={handlePhotosChange}
              accept="image/*"
              style={{ display: 'none' }}
            />
          </div>
          <Grid2 container spacing={2}>
            {existing_photos.map((photoURL, index) => (
              <Grid2 item key={photoURL.id}>
                <img 
                  src={photoURL instanceof File ? URL.createObjectURL(photoURL) : photoURL.photoURL}
                  alt={`${index + 1} of the apartment`}
                  style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                />
                <IconButton 
                  aria-label="delete"
                  color="primary"
                  onClick={() => handleDeleteExistingPhoto(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Grid2>
            ))}
            {uploaded_photos.map((photoURL, index) => (
              <Grid2 item key={index}>
                <img 
                  src={URL.createObjectURL(photoURL)}
                  alt={`${index + 1} of the apartment`}
                  style={{ width: '100px', height: '100px', objectFit: 'cover' }}
                />
                <IconButton 
                  aria-label="delete"
                  color="primary"
                  onClick={() => handleDeletePhoto(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Grid2>
            ))}
          </Grid2>
            <Button type="submit" variant="contained" color="primary">
              {t('editApartment.save')}
            </Button>
        </form>
        )}
        {tabIndex === 1 && (
          <ParkingTypesForm apartmentId={id} token={token} />
        )}
        {tabIndex === 2 && (
          <ApartmentEquipmentForm apartmentId={id} token={token} />
        )}
        {tabIndex === 3 && (
          <KitchenEquipmentForm apartmentId={id} token={token} />
        )}
        {tabIndex === 4 && (
          <HomeGoodsForm apartmentId={id} token={token} />
        )}
        {tabIndex === 5 && (
          <RentPreferencesForm apartmentId={id} token={token} />
        )}
        {tabIndex === 6 && (
          <RentOfferForm apartmentId={id} token={token} />
        )}
      </Box>
      </Container>
      <Footer />
    </div>
  );
};

export default EditApartment;
